import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const HERO_BG = 'http://geel.fp.08wx.cn/images/f4807ff4be391d5f80d16e1f627bf083.jpg';

export default function HeroSection() {
  const { t } = useTranslation();

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src={HERO_BG}
          alt="blockchain network background"
          className="w-full h-full object-cover object-top"
        />
        {/* Light overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/70 to-white/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-white/50 via-transparent to-white/10" />
      </div>

      {/* Grid overlay */}
      <div className="absolute inset-0 grid-bg opacity-40" />

      {/* Floating glow orbs */}
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float-slow pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-accent/10 rounded-full blur-3xl animate-float pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 pt-24 pb-20">
        <div className="max-w-3xl">
          {/* Tag */}
          <div className="inline-flex items-center gap-2 mb-8 px-4 py-2 bg-primary/10 border border-primary/25 rounded-full">
            <span className="w-2 h-2 rounded-full bg-primary animate-glow-pulse flex-shrink-0" />
            <span className="text-primary text-sm font-medium">{t('hero.tag')}</span>
          </div>

          {/* Main Title */}
          <h1 className="text-5xl md:text-6xl xl:text-7xl font-heading font-bold leading-tight mb-6">
            <span className="gradient-text block">{t('hero.title1')}</span>
            <span className="text-gray-800 block mt-1">{t('hero.title2')}</span>
          </h1>

          {/* Subtitle */}
          <p className="text-gray-500 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl">
            {t('hero.subtitle')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 mb-16">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary-dark text-white font-bold text-base rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {t('hero.cta1')}
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-8 py-4 border border-primary/40 hover:border-primary text-primary hover:bg-primary/5 text-base font-semibold rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer bg-white/60"
            >
              {t('hero.cta2')}
              <i className="ri-external-link-line text-sm" />
            </Link>
          </div>

          {/* Mini Stats Row */}
          <div className="flex flex-wrap gap-6 items-center">
            {[
              { num: t('stats.years'), label: t('stats.years.label') },
              { num: t('stats.clients'), label: t('stats.clients.label') },
              { num: t('stats.projects'), label: t('stats.projects.label') },
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-3">
                {i > 0 && <div className="w-px h-8 bg-primary/20 hidden sm:block" />}
                <div>
                  <div className="text-2xl font-heading font-bold text-primary leading-none">{stat.num}</div>
                  <div className="text-xs text-gray-400 mt-0.5 whitespace-nowrap">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-60">
        <span className="text-xs text-gray-400 tracking-widest uppercase">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-primary/60 to-transparent" />
      </div>
    </section>
  );
}
